import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Plus, PencilLine, CaretDown, CaretRight, CaretLeft, Trash, X, Copy, Image as ImageIcon, Table as TableIcon,
} from "@phosphor-icons/react";
import api, { apiError, API_BASE } from "../lib/api";
import { zar } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const EMPTY_ITEM = { name: "", price: "", recipe_id: "", category_id: "", archived: false };

export default function PosMenu() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [section, setSection] = useState(null);       // active top-level section id
  const [expanded, setExpanded] = useState({});
  const [catDialog, setCatDialog] = useState(null);   // {mode:'add'|'edit', parent_id, cat}
  const [catForm, setCatForm] = useState({ name: "", colour: "#A78BFA", parent_id: null });
  const [copySrc, setCopySrc] = useState(null);
  const [copyTarget, setCopyTarget] = useState("root");
  const [itemDialog, setItemDialog] = useState(false);
  const [itemEditing, setItemEditing] = useState(null);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM);
  const [imgFile, setImgFile] = useState(null);
  const [tDialog, setTDialog] = useState(null);       // {mode} table admin
  const [tForm, setTForm] = useState({ name: "", archived: false });
  const [showTables, setShowTables] = useState(false);

  const load = () => {
    api.get("/pos/categories").then((r) => setCats(r.data)).catch(() => toast.error("Failed to load categories"));
    api.get("/pos/menu").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load menu"));
    api.get("/pos/tables").then((r) => setTables(r.data)).catch(() => {});
    api.get("/recipes?type=dish&status=active").then((r) => setRecipes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const roots = useMemo(() => cats.filter((c) => !c.parent_id && !c.archived).sort((a, b) => a.sort - b.sort), [cats]);
  const active = section || roots[0]?.id || null;
  const childrenOf = (id) => cats.filter((c) => c.parent_id === id && !c.archived).sort((a, b) => a.sort - b.sort);
  const itemsOf = (cid) => items.filter((i) => i.category_id === cid && !i.archived);
  const archivedItems = items.filter((i) => i.archived);
  const leafOptions = cats.filter((c) => !c.archived);

  const swapSort = async (list, idx, dir) => {
    const other = list[idx + dir];
    if (!other) return;
    try {
      const a = list[idx];
      await api.put(`/pos/categories/${a.id}`, { ...a, sort: other.sort });
      await api.put(`/pos/categories/${other.id}`, { ...other, sort: a.sort });
      load();
    } catch (e) {
      toast.error(apiError(e, "Reorder failed"));
    }
  };

  const swapItemSort = async (list, idx, dir) => {
    const other = list[idx + dir];
    if (!other) return;
    const a = list[idx];
    try {
      await api.put(`/pos/menu/${a.id}`, { ...a, sort: other.sort ?? 0 });
      await api.put(`/pos/menu/${other.id}`, { ...other, sort: a.sort ?? 0 });
      load();
    } catch (e) {
      toast.error(apiError(e, "Reorder failed"));
    }
  };

  const saveCat = async () => {
    if (!catForm.name.trim()) return toast.error("Name required");
    try {
      if (catDialog.mode === "edit") {
        const maxSort = Math.max(0, ...cats.filter((c) => c.parent_id === (catForm.parent_id || null)).map((c) => c.sort));
        await api.put(`/pos/categories/${catDialog.cat.id}`, { ...catForm, sort: catDialog.cat.sort ?? maxSort, archived: !!catDialog.cat.archived });
      } else {
        const sibs = cats.filter((c) => (c.parent_id || null) === (catForm.parent_id || null));
        await api.post("/pos/categories", { ...catForm, sort: sibs.length });
      }
      toast.success("Saved");
      setCatDialog(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    }
  };

  const delCat = async (c) => {
    try {
      await api.delete(`/pos/categories/${c.id}`);
      toast.success(`"${c.name}" deleted`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Delete failed"));
    }
  };

  const doCopy = async () => {
    try {
      await api.post(`/pos/categories/${copySrc.id}/copy`, { table_id: copyTarget === "root" ? "" : copyTarget });
      toast.success(`"${copySrc.name}" copied`);
      setCopySrc(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Copy failed"));
    }
  };

  const pickDish = (rid) => {
    const r = recipes.find((x) => x.id === rid);
    if (r) setItemForm((f) => ({ ...f, recipe_id: rid, name: f.name || r.name, price: r.selling_price ?? f.price }));
    else setItemForm((f) => ({ ...f, recipe_id: "" }));
  };

  const saveItem = async () => {
    if (!itemForm.name.trim()) return toast.error("Name required");
    const payload = { ...itemForm, price: Number(itemForm.price) || 0, recipe_id: itemForm.recipe_id || null,
                      category_id: itemForm.category_id || null, sort: itemEditing?.sort ?? itemsOf(itemForm.category_id).length };
    try {
      let id = itemEditing?.id;
      if (itemEditing) await api.put(`/pos/menu/${id}`, payload);
      else id = (await api.post("/pos/menu", payload)).data.id;
      if (imgFile) {
        const fd = new FormData();
        fd.append("file", imgFile);
        await api.post(`/pos/menu/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(itemEditing ? "Item updated" : "Item added");
      setItemDialog(false);
      setImgFile(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    }
  };

  const delItem = async (i) => {
    try {
      await api.put(`/pos/menu/${i.id}`, { ...i, archived: true });
      toast.success(`"${i.name}" archived`);
      setItemDialog(false);
      load();
    } catch (e) {
      toast.error(apiError(e, "Archive failed"));
    }
  };

  const saveTable = async () => {
    if (!tForm.name.trim()) return toast.error("Table name required");
    try {
      if (tDialog.mode === "edit") await api.put(`/pos/tables/${tDialog.table.id}`, tForm);
      else await api.post("/pos/tables", tForm);
      toast.success("Saved");
      setTDialog(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    }
  };

  const catBlock = (c, depth = 0) => {
    const kids = childrenOf(c.id);
    const its = itemsOf(c.id);
    const open = expanded[c.id] !== false;
    const sibs = childrenOf(c.parent_id || active && cats.find((x) => x.id === c.id)?.parent_id);
    return (
      <div key={c.id} className={`border border-border rounded-lg bg-white ${depth ? "ml-6" : ""}`} data-testid={`cat-block-${c.id}`}>
        <div className="flex items-center gap-2 px-3 py-2.5">
          <button onClick={() => setExpanded({ ...expanded, [c.id]: !open })} data-testid={`cat-toggle-${c.id}`} className="text-muted-foreground hover:text-foreground">
            {open ? <CaretDown size={15} /> : <CaretRight size={15} />}
          </button>
          <input type="color" value={c.colour || "#A78BFA"} data-testid={`cat-colour-${c.id}`} title="Category colour"
            onChange={async (e) => { await api.put(`/pos/categories/${c.id}`, { ...c, colour: e.target.value }); load(); }}
            className="h-7 w-8 cursor-pointer rounded border border-border bg-white p-0.5" />
          <Input className="h-8 font-medium flex-1 min-w-[120px]" value={c.name} data-testid={`cat-name-${c.id}`}
            onChange={(e) => setCats(cats.map((x) => (x.id === c.id ? { ...x, name: e.target.value } : x)))}
            onBlur={async (e) => { if (e.target.value.trim() && e.target.value !== c.name) { try { await api.put(`/pos/categories/${c.id}`, { ...c, name: e.target.value.trim() }); load(); } catch (err) { toast.error(apiError(err, "Rename failed")); load(); } } }} />
          <Badge variant="outline" className="text-[10px] shrink-0" data-testid={`cat-count-${c.id}`}>{its.length} item(s)</Badge>
          <div className="flex items-center">
            <Button size="sm" variant="ghost" data-testid={`cat-up-${c.id}`} onClick={() => swapSort(childrenOf(c.parent_id || null), childrenOf(c.parent_id || null).findIndex((x) => x.id === c.id), -1)}><CaretRight size={13} className="-rotate-90" /></Button>
            <Button size="sm" variant="ghost" data-testid={`cat-down-${c.id}`} onClick={() => swapSort(childrenOf(c.parent_id || null), childrenOf(c.parent_id || null).findIndex((x) => x.id === c.id), 1)}><CaretDown size={13} /></Button>
            <Button size="sm" variant="ghost" data-testid={`cat-copy-${c.id}`} title="Copy this category (and items) to another section" onClick={() => { setCopySrc(c); setCopyTarget("root"); }}><Copy size={14} /></Button>
            <Button size="sm" variant="ghost" data-testid={`cat-del-${c.id}`} className="text-destructive hover:text-destructive" onClick={() => delCat(c)}><X size={14} /></Button>
          </div>
        </div>
        {open && (
          <div className="border-t px-3 py-2.5 space-y-2">
            {kids.map((k) => catBlock(k, depth + 1))}
            {its.map((i, idx) => (
              <div key={i.id} className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2" data-testid={`menu-item-row-${i.id}`}>
                {i.has_image && <img src={`${API_BASE}/api/pos/menu/${i.id}/image`} alt="" className="h-8 w-8 rounded object-cover" />}
                <span className="flex-1 font-medium text-sm">{i.name}</span>
                <Badge variant="outline" className="text-[10px]" style={{ borderColor: `${c.colour}80`, color: c.colour }}>{c.name}</Badge>
                {i.recipe_id && <Badge variant="outline" className="text-[10px]" data-testid={`menu-item-recipe-badge-${i.id}`}>{recipes.find((r) => r.id === i.recipe_id)?.name || "Dish linked"}</Badge>}
                <span className="num text-sm font-semibold w-20 text-right">{zar(i.price)}</span>
                <Button size="sm" variant="ghost" data-testid={`item-up-${i.id}`} onClick={() => swapItemSort(its, idx, -1)}><CaretRight size={13} className="-rotate-90" /></Button>
                <Button size="sm" variant="ghost" data-testid={`item-down-${i.id}`} onClick={() => swapItemSort(its, idx, 1)}><CaretDown size={13} /></Button>
                <Button size="sm" variant="ghost" data-testid={`item-edit-${i.id}`}
                  onClick={() => { setItemEditing(i); setItemForm({ name: i.name, price: i.price, recipe_id: i.recipe_id || "", category_id: i.category_id || "", archived: false }); setImgFile(null); setItemDialog(true); }}>
                  <PencilLine size={14} />
                </Button>
                <Button size="sm" variant="ghost" data-testid={`item-del-${i.id}`} className="text-destructive hover:text-destructive" onClick={() => delItem(i)}><X size={14} /></Button>
              </div>
            ))}
            <button onClick={() => { setItemEditing(null); setItemForm({ ...EMPTY_ITEM, category_id: c.id }); setImgFile(null); setItemDialog(true); }}
              data-testid={`cat-add-item-${c.id}`}
              className="w-full rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent transition-colors">
              + Add item to {c.name}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="pos-menu-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Menu Builder</h1>
          <p className="text-muted-foreground mt-1">Sections → nested categories → items. Prices VAT-inclusive. Link a dish recipe for stock depletion.</p>
        </div>
        <Button variant="outline" size="sm" data-testid="tables-admin-toggle" onClick={() => setShowTables(!showTables)}>
          <TableIcon size={15} className="mr-1" /> Tables ({tables.filter((t) => !t.archived).length})
        </Button>
      </div>

      {showTables && (
        <div className="bg-white border border-border rounded-lg p-4 space-y-3" data-testid="pos-tables-admin">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">Tables</h2>
            <Button variant="outline" size="sm" data-testid="table-add-btn" onClick={() => { setTDialog({ mode: "add" }); setTForm({ name: "", archived: false }); }}>
              <Plus size={14} className="mr-1" /> Add table
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {tables.map((t) => (
              <button key={t.id} onClick={() => { setTDialog({ mode: "edit", table: t }); setTForm({ name: t.name, archived: !!t.archived }); }}
                data-testid={`table-edit-${t.id}`}
                className={`rounded-md border px-3 py-2 text-sm transition-colors hover:bg-accent ${t.archived ? "opacity-50 border-dashed" : "border-border"}`}>
                {t.name}{t.archived && " (archived)"}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* section tabs */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="section-tabs">
        {roots.map((s, idx) => (
          <button key={s.id} onClick={() => setSection(s.id)} data-testid={`section-tab-${s.id}`}
            className={`rounded-t-lg border border-b-0 px-4 py-2.5 text-sm font-semibold transition-colors ${active === s.id ? "bg-white border-border" : "border-transparent text-muted-foreground hover:bg-white/60"}`}>
            <span className="inline-block h-2.5 w-2.5 rounded-sm mr-2 align-middle" style={{ backgroundColor: s.colour }} />
            {s.name}
          </button>
        ))}
        <button onClick={() => { setCatDialog({ mode: "add" }); setCatForm({ name: "", colour: "#A78BFA", parent_id: null }); }}
          data-testid="section-add"
          className="rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent transition-colors">
          <Plus size={14} className="inline" /> Section
        </button>
        {active && roots.length > 0 && (
          <div className="ml-2 flex items-center gap-0.5">
            <Button size="sm" variant="ghost" data-testid="section-left" title="Move section left" onClick={() => swapSort(roots, roots.findIndex((r) => r.id === active), -1)}><CaretLeft size={14} /></Button>
            <Button size="sm" variant="ghost" data-testid="section-right" title="Move section right" onClick={() => swapSort(roots, roots.findIndex((r) => r.id === active), 1)}><CaretRight size={14} /></Button>
            <Button size="sm" variant="ghost" data-testid="section-del" className="text-destructive hover:text-destructive" title="Delete section (must be empty)"
              onClick={() => delCat(roots.find((r) => r.id === active))}><Trash size={14} /></Button>
          </div>
        )}
      </div>

      <div className="bg-muted/40 border border-border rounded-b-lg rounded-tr-lg p-4 space-y-3" data-testid="section-body">
        {!active && <p className="text-sm text-muted-foreground py-6 text-center">Create your first section (e.g. "Food Menus", "Wines") with the + button above.</p>}
        {active && childrenOf(active).map((c) => catBlock(c))}
        {active && (
          <button onClick={() => { setCatDialog({ mode: "add" }); setCatForm({ name: "", colour: "#A78BFA", parent_id: active }); }}
            data-testid="category-add"
            className="w-full rounded-lg border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground hover:bg-white transition-colors">
            + Add category in {roots.find((r) => r.id === active)?.name}
          </button>
        )}
      </div>

      {archivedItems.length > 0 && (
        <div className="bg-white border border-border rounded-lg p-4" data-testid="archived-items">
          <p className="text-xs text-muted-foreground mb-2">Archived items</p>
          <div className="flex flex-wrap gap-2">
            {archivedItems.map((i) => (
              <button key={i.id} data-testid={`archived-item-${i.id}`}
                onClick={async () => { await api.put(`/pos/menu/${i.id}`, { ...i, archived: false }); load(); toast.success(`"${i.name}" restored`); }}
                className="rounded-md border border-dashed border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent" title="Click to restore">
                {i.name} · {zar(i.price)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* category add/edit */}
      <Dialog open={!!catDialog} onOpenChange={() => setCatDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="category-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">{catDialog?.mode === "edit" ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="category-name" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} placeholder="e.g. A'la Carte, Starters" /></div>
            <div className="space-y-1.5">
              <Label>Parent (blank = top-level section)</Label>
              <Select value={catForm.parent_id || "root"} onValueChange={(v) => setCatForm({ ...catForm, parent_id: v === "root" ? null : v })}>
                <SelectTrigger data-testid="category-parent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="root">— Top-level section —</SelectItem>
                  {leafOptions.filter((c) => c.id !== catDialog?.cat?.id).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.path}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Colour tag</Label>
              <input type="color" data-testid="category-colour" value={catForm.colour} onChange={(e) => setCatForm({ ...catForm, colour: e.target.value })}
                className="h-9 w-14 cursor-pointer rounded-md border border-border bg-white p-1" />
            </div>
            <Button onClick={saveCat} data-testid="category-save" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* copy category */}
      <Dialog open={!!copySrc} onOpenChange={() => setCopySrc(null)}>
        <DialogContent className="max-w-sm" data-testid="copy-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Copy "{copySrc?.name}"</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Duplicates the category, its sub-categories and its items.</p>
            <div className="space-y-1.5">
              <Label>Copy into section</Label>
              <Select value={copyTarget} onValueChange={setCopyTarget}>
                <SelectTrigger data-testid="copy-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="root">— As a new top-level section —</SelectItem>
                  {roots.filter((r) => r.id !== copySrc?.id).map((r) => (
                    <SelectItem key={r.id} value={r.id} data-testid={`copy-target-${r.id}`}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={doCopy} data-testid="copy-confirm" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">Copy</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* item add/edit */}
      <Dialog open={itemDialog} onOpenChange={setItemDialog}>
        <DialogContent className="max-w-sm" data-testid="menu-item-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">{itemEditing ? "Edit item" : "Add item"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Link from Dish recipe (auto-fills name &amp; price)</Label>
              <Select value={itemForm.recipe_id || "none"} onValueChange={(v) => pickDish(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="menu-item-recipe"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No recipe link</SelectItem>
                  {recipes.map((r) => (
                    <SelectItem key={r.id} value={r.id} data-testid={`menu-item-recipe-${r.id}`}>{r.name} · {zar(r.selling_price || 0)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="menu-item-name" value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Price (R, VAT-inclusive)</Label><Input type="number" min="0" step="any" data-testid="menu-item-price" value={itemForm.price} onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={itemForm.category_id || "none"} onValueChange={(v) => setItemForm({ ...itemForm, category_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="menu-item-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Uncategorised</SelectItem>
                  {leafOptions.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`menu-item-cat-${c.id}`}>{c.path}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tile image (optional)</Label>
              <label className="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground cursor-pointer hover:bg-accent" data-testid="menu-item-image-label">
                <ImageIcon size={16} />
                {imgFile ? imgFile.name : itemEditing?.has_image ? "Replace current image…" : "Upload image…"}
                <input type="file" accept="image/*" className="hidden" data-testid="menu-item-image" onChange={(e) => setImgFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <Button onClick={saveItem} data-testid="menu-item-save" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">{itemEditing ? "Save changes" : "Add item"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* table add/edit */}
      <Dialog open={!!tDialog} onOpenChange={() => setTDialog(null)}>
        <DialogContent className="max-w-xs" data-testid="table-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">{tDialog?.mode === "edit" ? "Edit table" : "Add table"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Name / number</Label><Input data-testid="table-name" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} placeholder="e.g. Table 4, Patio 2" /></div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="table-archived" checked={tForm.archived} onChange={(e) => setTForm({ ...tForm, archived: e.target.checked })} />
              Archived
            </label>
            <Button onClick={saveTable} data-testid="table-save" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
